import React, { useEffect, useState } from "react";
import { Loader2, Moon } from "lucide-react";
import { toast } from "sonner";
import { SERIF, SOUNDSCAPES, getNight, makeNight } from "./practiceApi";
import { PracticePlayer } from "./PracticePlayer";

// One-tap Night Version: Soulumo Selected is pre-chosen; shows progress until the night track is ready.
export const NightCreator = ({ journeyId, onReady, compact, fresh }) => {
  const [n, setN] = useState(undefined);
  const [started, setStarted] = useState(false);
  const [scape, setScape] = useState("selected");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getNight(journeyId).then(setN).catch(() => setN(null)); }, [journeyId]);
  useEffect(() => {
    if (!n || !["queued", "building"].includes(n.status)) { if (n?.status === "ready") onReady?.(n); return undefined; }
    const t = setInterval(() => getNight(journeyId).then(setN).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [n, journeyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async () => {
    setBusy(true);
    try { setN(await makeNight(journeyId, scape)); setStarted(true); }
    catch (e) { toast.error(e?.response?.data?.detail === "membership_required" ? "Night Versions are part of Soulumo membership." : "Couldn't start your Night Version. Please try again."); }
    setBusy(false);
  };
  if (n === undefined) return null;
  if (n && ["queued", "building"].includes(n.status)) {
    return (
      <div className="flex items-center gap-3 text-sm text-[#cfc6ae]" data-testid="night-building">
        <Loader2 size={16} className="animate-spin text-[#d7bb7a]" /> Creating your Night Version… this takes a few minutes. You can leave this page.
      </div>
    );
  }
  if (n?.status === "ready" && (!fresh || started)) {
    return (
      <div data-testid="night-ready">
        <p className="mb-2 text-xs uppercase tracking-[0.22em] text-[#9fb39a]">Your Night Version is ready</p>
        <PracticePlayer kind="night" src={n.audio_url} title={`Night Installation · ${n.label}`} />
      </div>
    );
  }
  return (
    <div data-testid="night-creator">
      {!compact && <>
        <h3 className="flex items-center gap-2 text-2xl text-[#f3ecd9]" style={{ fontFamily: SERIF }}><Moon size={18} className="text-[#d7bb7a]" /> Create a Night Version</h3>
        <p className="mt-2 text-sm text-[#cfc6ae]">We'll turn your installation into a gentle 3-hour sleep experience: Adelaida helps you let go, then soft relaxation, then your own words as quiet whispers within restful sound.</p>
      </>}
      <div className="mt-4 flex flex-wrap gap-2">
        {SOUNDSCAPES.map(([id, label, tag]) => (
          <button key={id} type="button" onClick={() => setScape(id)} data-testid={`night-scape-${id}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${scape === id ? "border-[#d7bb7a] bg-[#d7bb7a]/20 text-[#f3ecd9]" : "border-[#2b3d5e] text-[#cfc6ae] hover:border-[#d7bb7a]/60"}`}>
            {label}{tag && <span className="ml-1 text-[#d7bb7a]">· {tag}</span>}
          </button>
        ))}
      </div>
      {n?.status === "failed" && <p className="mt-2 text-xs text-[#d7a77a]">The last attempt didn't finish. Tap below to try again.</p>}
      <button type="button" onClick={go} disabled={busy} data-testid="night-create-btn"
        className="mt-4 w-full rounded-full border border-[#d7bb7a] bg-[#d7bb7a]/15 py-3 text-xs uppercase tracking-[0.26em] text-[#f3ecd9] transition-colors hover:bg-[#d7bb7a]/30 disabled:opacity-50">
        {busy ? "Starting…" : fresh && n?.status === "ready" ? "Make a new Night Version" : "Create Night Version"}
      </button>
    </div>
  );
};
